import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';
import { formatCurrency } from '../utils/currency';

interface SellerListItem {
  id: string;
  userId: string;
  code: string;
  name: string;
  email: string;
  document?: string;
  phone?: string;
  commissionRate: number;
  status: 'active' | 'inactive';
  balance: number;
  salesCount: number;
  createdAt: string;
}

interface StatementPeriod {
  periodMonth: string;
  totalAmount: number;
  commissionCount: number;
  paid: boolean;
  paidAt?: string;
}

export interface SellerStatement {
  periodMonth: string;
  totalAmount: number;
  commissionCount: number;
  paid: boolean;
  paidAt?: string;
  commissions: Array<{
    id: string;
    customerName?: string;
    subscriptionAmount: number;
    commissionRate: number;
    commissionAmount: number;
    payoutId?: string;
    createdAt: string;
  }>;
  byDay: Array<{ date: string; count: number; amount: number }>;
}

interface Credentials {
  email: string;
  tempPassword: string;
  code?: string;
}

const formatInt = (n: number) => n.toLocaleString('pt-BR');

const formatRate = (rate: number) =>
  `${rate.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;

/** 'YYYY-MM-01' -> 'agosto de 2026', built from the parts so there is no timezone shift. */
export const formatMonth = (periodMonth: string) => {
  const [year, month] = periodMonth.split('-');
  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });
};

/** 'YYYY-MM-DD' -> 'DD/MM/YYYY' by slicing, for the same reason. */
export const formatDay = (date: string) => {
  const [year, month, day] = date.split('-');
  return `${day}/${month}/${year}`;
};

export const SellerManagementPage: React.FC = () => {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<SellerListItem | null>(null);
  const [credentials, setCredentials] = useState<Credentials | null>(null);
  const [receiptsSeller, setReceiptsSeller] = useState<SellerListItem | null>(null);

  const sellersQuery = useQuery({
    queryKey: ['admin-sellers'],
    queryFn: async () => {
      const res = await api.get('/admin/sellers');
      return res.data as SellerListItem[];
    },
    // The admin should see balances rise as sales land, without a manual reload.
    refetchInterval: 30000,
  });

  const createMut = useMutation({
    mutationFn: async (data: FormState) => {
      const res = await api.post('/admin/sellers', {
        name: data.name,
        email: data.email,
        document: data.document,
        phone: data.phone,
        commissionRate: Number(data.commissionRate),
      });
      return res.data as { seller: SellerListItem; email: string; tempPassword: string };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['admin-sellers'] });
      setShowForm(false);
      setEditing(null);
      setCredentials({
        email: data.email,
        tempPassword: data.tempPassword,
        code: data.seller.code,
      });
    },
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: FormState }) => {
      const res = await api.patch(`/admin/sellers/${id}`, {
        name: data.name,
        document: data.document,
        phone: data.phone,
        commissionRate: Number(data.commissionRate),
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-sellers'] });
      setShowForm(false);
      setEditing(null);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/sellers/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-sellers'] }),
  });

  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'inactive' }) =>
      api.patch(`/admin/sellers/${id}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-sellers'] }),
  });

  const regenMut = useMutation({
    mutationFn: async (seller: SellerListItem) => {
      const res = await api.post(`/admin/sellers/${seller.id}/regenerate-password`);
      return { ...(res.data as { email: string; tempPassword: string }), code: seller.code };
    },
    onSuccess: (data) => setCredentials(data),
  });

  const handleSubmit = async (data: FormState) => {
    try {
      if (editing) {
        await updateMut.mutateAsync({ id: editing.id, data });
      } else {
        await createMut.mutateAsync(data);
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erro ao salvar vendedor');
    }
  };

  const handleDelete = async (s: SellerListItem) => {
    if (!confirm(`Excluir o vendedor ${s.name}? O histórico de comissões dele também será removido.`)) return;
    try {
      await deleteMut.mutateAsync(s.id);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erro ao excluir vendedor');
    }
  };

  const handleRegenerate = async (s: SellerListItem) => {
    if (!confirm(`Gerar nova senha temporária para ${s.name}? A senha atual deixará de funcionar.`)) return;
    try {
      await regenMut.mutateAsync(s);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erro ao gerar nova senha');
    }
  };

  const sellers = sellersQuery.data || [];
  const totalOwed = sellers.reduce((sum, s) => sum + s.balance, 0);
  const totalSales = sellers.reduce((sum, s) => sum + s.salesCount, 0);
  const activeCount = sellers.filter((s) => s.status === 'active').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Vendedores</h2>
          <p className="text-sm text-gray-600 mt-1">
            Cada vendedor tem um código de 5 dígitos. Quando um cliente assina informando esse
            código, o vendedor recebe a comissão configurada sobre a primeira assinatura.
          </p>
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
          className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-600 to-orange-500 text-white font-bold rounded-lg hover:from-orange-700 hover:to-orange-600 transition-all shadow-lg"
        >
          + Novo Vendedor
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white shadow rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Vendedores ativos</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{formatInt(activeCount)}</p>
        </div>
        <div className="bg-white shadow rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Vendas em aberto</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{formatInt(totalSales)}</p>
        </div>
        <div className="bg-white shadow rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Total a pagar</p>
          <p className="text-2xl font-bold text-orange-600 mt-1">{formatCurrency(totalOwed)}</p>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {sellersQuery.isLoading ? (
          <div className="p-12 text-center text-gray-500">Carregando…</div>
        ) : sellersQuery.error ? (
          <div className="p-12 text-center text-red-600">Erro ao carregar vendedores</div>
        ) : sellers.length === 0 ? (
          <div className="p-12 text-center text-gray-500">Nenhum vendedor cadastrado.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comissão</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendas em aberto</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Saldo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sellers.map((s) => (
                  <tr key={s.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{s.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-mono text-sm font-bold tracking-widest bg-orange-50 text-orange-700 px-2 py-1 rounded">
                        {s.code}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{s.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{formatRate(s.commissionRate)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{formatInt(s.salesCount)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                      {formatCurrency(s.balance)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          s.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {s.status === 'active' ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-3">
                      <button
                        onClick={() => setReceiptsSeller(s)}
                        className="text-orange-600 hover:text-orange-700 font-medium"
                      >
                        Recibos
                      </button>
                      <button
                        onClick={() => {
                          setEditing(s);
                          setShowForm(true);
                        }}
                        className="text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() =>
                          statusMut.mutate({
                            id: s.id,
                            status: s.status === 'active' ? 'inactive' : 'active',
                          })
                        }
                        className="text-gray-600 hover:text-gray-800 font-medium"
                      >
                        {s.status === 'active' ? 'Desativar' : 'Ativar'}
                      </button>
                      <button
                        onClick={() => handleRegenerate(s)}
                        className="text-purple-600 hover:text-purple-700 font-medium"
                      >
                        Nova senha
                      </button>
                      <button
                        onClick={() => handleDelete(s)}
                        className="text-red-600 hover:text-red-700 font-medium"
                      >
                        Excluir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <SellerFormModal
          seller={editing}
          onCancel={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onSubmit={handleSubmit}
          isSubmitting={createMut.isPending || updateMut.isPending}
        />
      )}

      {credentials && (
        <SellerCredentialsModal credentials={credentials} onClose={() => setCredentials(null)} />
      )}

      {receiptsSeller && (
        <SellerReceiptsModal seller={receiptsSeller} onClose={() => setReceiptsSeller(null)} />
      )}
    </div>
  );
};

interface FormState {
  name: string;
  email: string;
  document: string;
  phone: string;
  commissionRate: string;
}

const SellerFormModal: React.FC<{
  seller: SellerListItem | null;
  onCancel: () => void;
  onSubmit: (data: FormState) => void;
  isSubmitting: boolean;
}> = ({ seller, onCancel, onSubmit, isSubmitting }) => {
  const [form, setForm] = useState<FormState>({
    name: seller?.name || '',
    email: seller?.email || '',
    document: seller?.document || '',
    phone: seller?.phone || '',
    commissionRate: seller ? String(seller.commissionRate) : '10',
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
        <h3 className="text-xl font-bold mb-4">{seller ? 'Editar Vendedor' : 'Novo Vendedor'}</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(form);
          }}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700">Nome</label>
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              required
              type="email"
              disabled={!!seller}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100"
            />
            {seller && (
              <p className="text-xs text-gray-500 mt-1">O email de acesso não pode ser alterado.</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">CPF/CNPJ</label>
              <input
                value={form.document}
                onChange={(e) => setForm({ ...form, document: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Telefone</label>
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Comissão (%)</label>
            <input
              required
              type="number"
              min={0}
              max={100}
              step="0.01"
              value={form.commissionRate}
              onChange={(e) => setForm({ ...form, commissionRate: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Percentual do valor da assinatura. Alterar afeta apenas vendas futuras — comissões já
              registradas mantêm o percentual da época.
            </p>
          </div>
          {!seller && (
            <p className="text-xs text-gray-500">
              O código de 5 dígitos e a senha temporária são gerados automaticamente.
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Salvando…' : seller ? 'Salvar' : 'Criar vendedor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SellerCredentialsModal: React.FC<{
  credentials: Credentials;
  onClose: () => void;
}> = ({ credentials, onClose }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(credentials.tempPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <h3 className="text-xl font-bold mb-2">Acesso do vendedor</h3>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 text-sm text-yellow-900">
          <strong>Atenção:</strong> esta senha não será mostrada novamente. Copie agora e envie ao
          vendedor. No primeiro acesso ele será obrigado a definir a senha dele.
        </div>
        <div className="space-y-3 mb-4">
          {credentials.code && (
            <div>
              <span className="text-xs text-gray-500">Código de vendas</span>
              <div className="font-mono text-2xl font-bold tracking-widest text-orange-700 bg-orange-50 px-3 py-2 rounded text-center">
                {credentials.code}
              </div>
            </div>
          )}
          <div>
            <span className="text-xs text-gray-500">Email</span>
            <div className="font-mono text-sm bg-gray-50 px-3 py-2 rounded break-all">
              {credentials.email}
            </div>
          </div>
          <div>
            <span className="text-xs text-gray-500">Senha temporária</span>
            <div className="flex items-center gap-2">
              <div className="flex-1 font-mono text-lg bg-gray-50 px-3 py-2 rounded">
                {credentials.tempPassword}
              </div>
              <button
                onClick={copy}
                className="px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 whitespace-nowrap"
              >
                {copied ? 'Copiado!' : 'Copiar'}
              </button>
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-full px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900"
        >
          Fechar
        </button>
      </div>
    </div>
  );
};

const SellerReceiptsModal: React.FC<{
  seller: SellerListItem;
  onClose: () => void;
}> = ({ seller, onClose }) => {
  const qc = useQueryClient();
  const [openPeriod, setOpenPeriod] = useState<string | null>(null);

  const detailQuery = useQuery({
    queryKey: ['admin-seller-detail', seller.id],
    queryFn: async () => {
      const res = await api.get(`/admin/sellers/${seller.id}`);
      return res.data as { periods: StatementPeriod[]; balance: number };
    },
  });

  const statementQuery = useQuery({
    queryKey: ['admin-seller-statement', seller.id, openPeriod],
    queryFn: async () => {
      const res = await api.get(`/admin/sellers/${seller.id}/statement`, {
        params: { periodMonth: openPeriod },
      });
      return res.data as SellerStatement;
    },
    enabled: !!openPeriod,
  });

  const payMut = useMutation({
    mutationFn: (periodMonth: string) =>
      api.post(`/admin/sellers/${seller.id}/payouts`, { periodMonth }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-sellers'] });
      qc.invalidateQueries({ queryKey: ['admin-seller-detail', seller.id] });
      qc.invalidateQueries({ queryKey: ['admin-seller-statement', seller.id] });
    },
  });

  const handlePay = async (period: StatementPeriod) => {
    const confirmed = confirm(
      `Confirmar o pagamento de ${formatCurrency(period.totalAmount)} a ${seller.name}, referente a ${formatMonth(period.periodMonth)}?\n\nO saldo desse mês será zerado.`
    );
    if (!confirmed) return;
    try {
      await payMut.mutateAsync(period.periodMonth);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erro ao registrar pagamento');
    }
  };

  const periods = detailQuery.data?.periods || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-xl font-bold">Recibos de {seller.name}</h3>
            <p className="text-sm text-gray-500">
              Código {seller.code} • Saldo em aberto:{' '}
              <span className="font-bold text-orange-600">
                {formatCurrency(detailQuery.data?.balance ?? seller.balance)}
              </span>
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">
            ×
          </button>
        </div>

        {detailQuery.isLoading ? (
          <div className="py-12 text-center text-gray-500">Carregando…</div>
        ) : periods.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            Este vendedor ainda não registrou vendas.
          </div>
        ) : (
          <ul className="space-y-3">
            {periods.map((p) => (
              <li key={p.periodMonth} className="border rounded-lg overflow-hidden">
                <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-3 bg-gray-50">
                  <div>
                    <div className="font-semibold capitalize">{formatMonth(p.periodMonth)}</div>
                    <div className="text-sm text-gray-600">
                      {formatInt(p.commissionCount)} venda{p.commissionCount === 1 ? '' : 's'} •{' '}
                      <span className="font-bold text-gray-900">{formatCurrency(p.totalAmount)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {p.paid ? (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        Pago
                        {p.paidAt ? ` em ${new Date(p.paidAt).toLocaleDateString('pt-BR')}` : ''}
                      </span>
                    ) : (
                      <button
                        onClick={() => handlePay(p)}
                        disabled={payMut.isPending}
                        className="px-3 py-1.5 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50"
                      >
                        Marcar como pago
                      </button>
                    )}
                    <button
                      onClick={() => setOpenPeriod(openPeriod === p.periodMonth ? null : p.periodMonth)}
                      className="px-3 py-1.5 border border-gray-300 text-sm rounded-lg hover:bg-white"
                    >
                      {openPeriod === p.periodMonth ? 'Fechar' : 'Ver recibo'}
                    </button>
                  </div>
                </div>

                {openPeriod === p.periodMonth && (
                  <div className="px-4 py-3 border-t">
                    {statementQuery.isLoading ? (
                      <p className="text-sm text-gray-500">Carregando recibo…</p>
                    ) : (
                      <StatementDetail statement={statementQuery.data} />
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

/** The receipt itself: per-day totals plus the individual sales. Shared with the seller's own page. */
export const StatementDetail: React.FC<{ statement?: SellerStatement }> = ({ statement }) => {
  if (!statement) return null;

  return (
    <div className="space-y-5">
      <div>
        <h5 className="text-sm font-semibold text-gray-700 mb-2">Vendas por dia</h5>
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 uppercase">
              <th className="py-1">Dia</th>
              <th className="py-1">Vendas</th>
              <th className="py-1 text-right">Comissão</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {statement.byDay.map((d) => (
              <tr key={d.date}>
                <td className="py-1.5">{formatDay(d.date)}</td>
                <td className="py-1.5">{formatInt(d.count)}</td>
                <td className="py-1.5 text-right">{formatCurrency(d.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 font-bold">
              <td className="py-2">Total</td>
              <td className="py-2">{formatInt(statement.commissionCount)}</td>
              <td className="py-2 text-right text-orange-600">{formatCurrency(statement.totalAmount)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div>
        <h5 className="text-sm font-semibold text-gray-700 mb-2">Detalhe das vendas</h5>
        <ul className="divide-y text-sm">
          {statement.commissions.map((c) => (
            <li key={c.id} className="py-1.5 flex justify-between gap-3">
              <span className="text-gray-700">
                {new Date(c.createdAt).toLocaleDateString('pt-BR')} —{' '}
                {c.customerName || 'Cliente removido'}
              </span>
              <span className="text-gray-900 font-medium whitespace-nowrap">
                {formatCurrency(c.commissionAmount)}{' '}
                <span className="text-gray-500 font-normal">
                  ({formatRate(c.commissionRate)} de {formatCurrency(c.subscriptionAmount)})
                </span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
